/**
 * @description 前端监控 SDK 客户端插件（.client.js 由 Nuxt 自动限定仅客户端加载）
 * @reason projectId/secretKey 从 runtimeConfig.public 读取（.env 由 Nuxt 注入），避免在客户端 bundle 中 process.env 取不到
 * 生产环境暂时禁用：monitor 项目未部署，不初始化、不上报；部署后移除 isProd 判断即可
 */
import FrontendMonitor from '@frontend-monitor/sdk'
import { nextTick } from 'vue'
import monitorConfig from '../monitor.config.js'
import { flushTrackQueue, getTrackDeviceId, setFM } from '~/utils/tracker'
import {
  clearTrackUserContext,
  getTrackUserContext,
  setTrackUserContext,
} from '~/utils/tracking/userContext'

const env = process.env.NUXT_PUBLIC_ENV || (import.meta.dev ? 'development' : 'production')

/**
 * @description 瑙ｅ喅褰撳墠 SDK 鏈彁渚?resolveConfig 鏃剁殑閰嶇疆鍚堝苟
 * @param {Object} config - 鐩戞帶閰嶇疆
 * @param {string} currentEnv - 褰撳墠鐜
 * @returns {Object}
 */
const resolveMonitorConfig = (config, currentEnv) => {
  const commonConfig = config?.common || {}
  const envConfig = config?.[currentEnv] || {}
  return {
    ...commonConfig,
    ...envConfig,
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  const publicConfig = runtimeConfig.public || {}

  const projectId = (publicConfig.monitorProjectId || '').trim()
  const secretKey = (publicConfig.monitorSecretKey || '').trim()
  const authCert = (publicConfig.monitorAuthCert || '').trim()
  const monitorEnabled = Boolean(projectId && (secretKey || authCert))

  if (!monitorEnabled && publicConfig.env !== 'production') {
    const reason = !projectId
      ? 'projectId 为空'
      : !secretKey && !authCert
        ? 'secretKey 与 authCert 均为空'
        : ''
    if (reason) {
      console.warn(
        '[FrontendMonitor] 未初始化:',
        reason,
        '| 当前 env:',
        publicConfig.env,
        '| 请确认构建/运行时已注入 NUXT_PUBLIC_MONITOR_PROJECT_ID 与 NUXT_PUBLIC_MONITOR_SECRET_KEY'
      )
    }
  }

  const baseOptions =
    typeof FrontendMonitor.resolveConfig === 'function'
      ? FrontendMonitor.resolveConfig(monitorConfig, env)
      : resolveMonitorConfig(monitorConfig, env)
  const options = {
    ...baseOptions,
    projectId,
    secretKey: secretKey || undefined,
    ...(authCert ? { authCert } : {}),
  }

  if (monitorEnabled) {
    FrontendMonitor.init(options)
  }

  setFM(monitorEnabled ? FrontendMonitor : null)

  const router = nuxtApp.$router
  if (monitorEnabled && nuxtApp.vueApp && router) {
    FrontendMonitor.integrateVue(nuxtApp.vueApp, { router })
  }

  router?.afterEach((to, from) => {
    flushTrackQueue()
    nextTick(() => {
      setTimeout(() => {
        const name = document.title?.trim() || to.meta?.title || to.name || to.path
        if (name) FrontendMonitor.setPageName(String(name))
        if (monitorEnabled && FrontendMonitor.getTransport?.()) {
          FrontendMonitor.track(
            'page_view',
            {
              url: to.fullPath || to.path || '',
              routeName: to.name ?? '',
              fromUrl: from?.fullPath || from?.path || '',
              ...getTrackUserContext(),
            },
            'view',
            name || to.path || ''
          )
        }
      }, 50)
    })
  })

  /**
   * @description 绑定监控 userId，并同步埋点客户类型上下文（供按客户类型查询）
   * @reason 未登录不能 setUser(null)：监控 UV「按用户ID去重」会丢掉匿名点击（如立即入驻）；回退 deviceId
   * @param {string|number|null|undefined} id
   * @param {Object|null|undefined} [userInfo] - 商城用户信息；登出传 null
   * @returns {void}
   */
  const setMonitorUser = (id, userInfo) => {
    const normalizedId =
      id !== undefined && id !== null && String(id).trim() !== '' ? String(id).trim() : ''
    // 未登录 / 登出：用稳定 deviceId 作为匿名 userId，保证 UV 与点击仍可统计
    const monitorUserId = normalizedId || getTrackDeviceId()
    if (FrontendMonitor && typeof FrontendMonitor.setUser === 'function') {
      FrontendMonitor.setUser(monitorUserId)
    }
    if (!normalizedId || userInfo === null) {
      clearTrackUserContext()
      return
    }
    if (userInfo && typeof userInfo === 'object') {
      setTrackUserContext(userInfo)
    }
  }

  nuxtApp.hook('app:mounted', () => {
    import('~/store/common/user.js')
      .then(({ useUserStore }) => {
        const userStore = useUserStore()
        if (userStore.isLoggedIn && userStore.userInfo) {
          const userId = userStore.userInfo.userId ?? userStore.userInfo.id ?? null
          if (userId) {
            setMonitorUser(userId, userStore.userInfo)
            return
          }
        }
        // 未登录也要绑定匿名 ID，否则「立即入驻」等点击 UV 不记
        setMonitorUser(null, null)
      })
      .catch(() => {
        setMonitorUser(null, null)
      })
  })

  return {
    provide: {
      monitor: FrontendMonitor,
      setMonitorUser,
    },
  }
})
